import React, { FC, ReactElement, memo } from 'react';
import { Theme, lighten } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Button from '@mui/material/Button';
import Rating from '@mui/material/Rating';
// Components
import { Grid, darken } from '@mui/material';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import MapView from '../map/bookingMap';
import Space, { Type } from '../layout/space';
import PassengerList from '../passengers/passengerList';
import ReservationLabelInfos from './reservationLabelInfos';
import BackButton from '../buttons/backButton';
import StopsTimeLineList from '../timeline/stopsTimelineList';
import SvgIcon from '../icons/SvgIcon';
import Text from '../text';
import EditButtonList, { EditButtonInfos } from '../profile/editButtonList';
import Separator from '../layout/separator';
import EditReservationMeetingPoint from '../../navigation/routes/reservations/editReservationMeetingPoint';
import EditReservationPassengers from '../../navigation/routes/reservations/editReservationPassengers';
// import EditReservationPayment from '../../navigation/routes/reservations/editReservationPayment';
// import EditReservationPoiNumber from '../../navigation/routes/reservations/editReservationPoiNumber';
import EditHistoryTipsAndRating from '../../navigation/routes/tripHistory/editHistoryTipsAndRating';
import EditReservationCostCenter from '../../navigation/routes/reservations/editReservationCostCenter';
import ReservationTracking from './reservationTracking';
import AccordionBox from '../accordionBox';
import RoundedIcon from '../icons/roundedIcon';
import TextCenterButton from '../buttons/textCenterButton';
import RadiusButton from '../buttons/radiusButton';
import LCAnimation from '../icons/LCAnimation';
import ModalButton, { ButtonType } from '../modals/templates/modalButton';
import InfoBox from '../infoBox';
import WickedImage from '../events/wicked/WickedImage';
// Resources
import { AddressType, Break, EventType, ExtraType, GetPoliciesOutput, Ride, StopType } from '../../core/apiTypes';
import { cronStringToDateAndDays, formatDateFromTimestamp } from '../../utils/date';
import { rps, stringsRes } from '../../resources/strings';
import {
    isBookingCancelled,
    findStop,
    getMaskedCard,
    canRateBooking,
    canSeeBookingRating,
    getRidePriceWithExtra,
    getIncomingStatus,
    getInProgressStatus,
    isRidePro,
    convertPaymentTypePolicy,
    isRideOnboardPayment,
    isBookingCancelledNoCharge,
    isBookingFinishedNotCanceled,
    getSOSStatus,
    isServiceGreen,
    getVehicleInfos,
    isAirport,
    getEncodedRoute,
} from '../../utils/business';
import { ModalType } from '../../core/modules/modals';
import { ModalSize } from '../modals/createModalHOC';
import { prepareWaypoints } from '../../utils/map';
import { paths } from '../../navigation/routes/paths';
import { Animation, IconAxel, ImageEvent } from '../../resources/images';
import { useBreakpoints, WebLocation } from '../../customHooks/useBreakpoints';
import { isWebView } from '../../utils/webview';
import { useGetPolicies } from '../../core/services/policies';
import useGlobalStyles from '../hook/useGlobalStyles';
import { useTheme } from '../../context/ThemeContext';
import colors, { darkColors } from '../../resources/style/colors';
import { callButtonBackground } from '../../resources/style/theme';
import { navigateTo } from '../../utils/redirect';
import { WEBSITE_GREEN_URL } from '../../utils/constants';
import { copyToClipboard } from '../../utils/htmlUtils';
import { useEvent } from '../../hooks/useEvent';
import { getFullnameMaxed } from '../../utils/user';

interface ReservationCardProps {
    title: string;
    reservation: Ride;
    menuEntries?: EditButtonInfos[];
    openModal?: (modalType: ModalType, props: any, id?: string) => any;
    onEditReservationFromDate?: () => void;
    onEditPoiNumber?: () => void;
    onEditReservationService?: () => void;
    onCancelBreak?: (currentBreak: Break) => void;
    onCancelSos?: () => void;
}

const ReservationCard: FC<ReservationCardProps> = (props): ReactElement => {
    const {
        reservation,
        title,
        menuEntries,
        openModal,
        onEditReservationFromDate,
        onEditPoiNumber,
        onCancelBreak,
        onCancelSos,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onEditReservationService,
    } = props;
    const {
        billing,
        booker,
        breaks,
        cost_center: costCenter,
        creation_date: creationDate,
        driver_note: driverNote,
        driver_rating: driverRating,
        driver,
        emergency,
        group,
        nameboard_option: hasPancarte,
        payment_method: paymentMethod,
        payment_policy: paymentPolicy,
        price_details: priceDetails,
        provision,
        ride_reason: rideReason,
        riders,
        schedule,
        service,
        start_date: plannedStartDate,
        status,
        stops,
        vehicle,
    } = reservation;

    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    const { currentEvent } = useEvent();

    const navigate = useNavigate();
    const webview = isWebView();
    const { mode } = useTheme();
    const isDarkMode = mode === 'dark';
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    const { data: groupPolicyDatas } = useGetPolicies<GetPoliciesOutput>({
        enabled: isBookingFinishedNotCanceled(status) && !!group?.uuid,
        variables: { groups: [group?.uuid] },
    });
    const groupPolicy = groupPolicyDatas?.group?.[0];

    const recurrent = !!schedule;
    const isPaused = breaks?.find((b) => {
        const now = Date.now() / 1000;
        return !b.deleted_at && now >= b.start_date && now <= b.end_date;
    });
    const willPaused = breaks?.find((b) => {
        const now = Date.now() / 1000;
        return !b.deleted_at && now < b.start_date && now <= b.end_date;
    });
    const pickup = findStop(stops, StopType.PICKUP);
    const price: string = getRidePriceWithExtra(reservation, priceDetails?.currency);
    const tips = priceDetails?.extras?.find((e) => e.type === ExtraType.TIPS);
    const canAddTip =
        !groupPolicy?.tip_not_allowed &&
        (!billing || (billing?.status !== 'issued' && billing?.status !== 'ready')) &&
        !tips &&
        (driverRating?.score == null || driverRating?.score > 3);

    const isPro = isRidePro(reservation);
    const isCanceled = isBookingCancelled(status);
    const isCanceledNoCharge = isBookingCancelledNoCharge(status);
    const isNotDispatched = getIncomingStatus().includes(status);
    const isInProgress = getInProgressStatus().includes(status);
    const isFinished = isBookingFinishedNotCanceled(status);
    const isTripHistoryPage = window.location.pathname.startsWith(paths.tripHistory);
    const isRideTrackingPage = window.location.pathname.includes(paths.homeTrackingMobile.replace(':id', ''));
    // const isRideNote = billing?.type?.toLocaleLowerCase() === 'ride_note';
    const isAssistance = reservation.is_assistance;
    const isPlane = isAirport(pickup);
    // const isTrain = isStation(pickup);
    const isOther = pickup?.address?.type === AddressType.OTHER;
    const isWicked = currentEvent === EventType.WICKED;
    const isGreen = !isWicked && isServiceGreen(service);
    const isOnboardPaid = isRideOnboardPayment(paymentMethod, paymentPolicy);

    const onChangeRider = (): void => {
        openModal(ModalType.EDITION, {
            title: stringsRes.booking.passengersTitle,
            children: <EditReservationPassengers />,
            modalStyle: classes.passengersEditModal,
            modalSize: ModalSize.xl,
            reservation,
        });
    };
    // const onChangePayment = (): void => {
    //     openModal(ModalType.EDITION, {
    //         title: stringsRes.booking.paymentTitle,
    //         subtitle: stringsRes.booking.selectPayment,
    //         children: <EditReservationPayment />,
    //         modalSize: ModalSize.xl,
    //         reservation,
    //     });
    // };
    const onShowOnboardPayment = (): void => {
        openModal(ModalType.ONBOARD_PAYMENT, {
            title: stringsRes.onboardPayment.title,
            modalSize: ModalSize.sm,
            ...(isMobileSize && { floatingClose: 'center', modalPosition: 'bottom' }),
            modalStyle: clsx(isMobileSize && globalClasses.modalBottomRating),
        });
    };

    const onChangeCostCenter = (): void => {
        openModal(ModalType.EDITION, {
            title: stringsRes.profile.costCenter,
            children: <EditReservationCostCenter />,
            modalSize: ModalSize.xl,
            reservation,
        });
    };
    const onChangePOINumber = (): void => {
        onEditPoiNumber();

        // openModal(ModalType.EDITION, {
        //     title: stringsRes.booking.planePlaceholder,
        //     children: <EditReservationPoiNumber />,
        //     modalSize: ModalSize.xl,
        //     reservation,
        // }); //modal edit is now nice to have feature
    };
    const onChangeMeetingPoint = (): void => {
        openModal(ModalType.EDITION, {
            title: stringsRes.booking.meetingPointEdit,
            subtitle: stringsRes.booking.meetingPointDetails,
            children: <EditReservationMeetingPoint />,
            modalSize: ModalSize.xl,
            labelConfirm: stringsRes.common.edit,
            reservation,
        });
    };

    const onAddTipsAndRating = (e?, newValue?): void => {
        if (canRateBooking(reservation)) {
            openModal(ModalType.EDITION, {
                title: stringsRes.tripHistory.tipsAndRating.title,
                floatingClose: isMobileSize ? 'topRight' : 'none',
                ...(isMobileSize && { modalPosition: 'bottom' }),
                children: <EditHistoryTipsAndRating />,
                modalSize: isMobileSize ? ModalSize.lg : ModalSize.xl,
                modalStyle: clsx(
                    globalClasses.modalRating,
                    //     isWebView() && classes.statusBarTop,
                    isMobileSize && globalClasses.modalBottomRating,
                ),
                labelConfirm: stringsRes.common.add,
                ride: reservation,
                stars: newValue,
                navigate,
            });
        }
    };

    const onNavigateToGreenMore = (): void => {
        navigateTo(null, WEBSITE_GREEN_URL);
    };

    const TextTitle = isMobileSize ? Text.H2 : Text.H0;
    const CustomSeparator = isRideTrackingPage ? <Separator height={1} /> : <Separator />;
    const hasRatingBlock = canRateBooking(reservation) || (canSeeBookingRating(reservation) && (driverRating || tips));
    const showRatingButton = canRateBooking(reservation) && (driverRating?.score == null || canAddTip);

    return (
        <>
            {webview && !isRideTrackingPage && <Space value={2} />}
            {!isMobileSize && <BackButton label={title} spaceBottom={1.5} />}

            {/* Top Infos */}
            {!isRideTrackingPage && (
                <div className={classes.headerRow}>
                    <TextTitle
                        text={
                            recurrent
                                ? cronStringToDateAndDays(schedule)
                                : formatDateFromTimestamp(plannedStartDate ?? creationDate)
                        }
                    />
                    {onEditReservationFromDate && !recurrent && (
                        <Button
                            id="edit"
                            className={clsx(classes.reservationLabelInfosButton, globalClasses.underline)}
                            onClick={onEditReservationFromDate}
                        >
                            {stringsRes.booking.edit}
                        </Button>
                    )}
                </div>
            )}
            {(!!isPaused || !!willPaused) && (
                <>
                    <Space value={3} type={Type.vertical} />
                    {!!isPaused && (
                        <Text.H2
                            text={`${stringsRes.recurrence.reservation.paused} ${formatDateFromTimestamp(
                                isPaused.end_date,
                                stringsRes.time.recurrenceFormat,
                            )}`}
                        />
                    )}
                    {!!willPaused && (
                        <Text.H2
                            text={`${stringsRes.recurrence.reservation.willPause} ${formatDateFromTimestamp(
                                willPaused.start_date,
                                stringsRes.time.recurrenceFormat,
                            )} ${stringsRes.time.to} ${formatDateFromTimestamp(
                                willPaused.end_date,
                                stringsRes.time.recurrenceFormat,
                            )}`}
                        />
                    )}
                    <Space value={2} type={Type.vertical} />
                    <ModalButton
                        id="cancelBreak"
                        type={ButtonType.CONFIRM}
                        onClick={() => onCancelBreak(isPaused || willPaused)}
                        fullWidth
                        label={
                            isPaused
                                ? stringsRes.reservation.unPauseRecurrent
                                : stringsRes.reservation.cancelPauseRecurrent
                        }
                    />
                </>
            )}

            {/* Map */}
            {(webview || isMobileSize) && getSOSStatus().includes(status) && emergency?.enabled && (
                <div className={classes.emergencyContainer}>
                    <LCAnimation animation={Animation.Loader} />
                    <div className={classes.centerDetails}>
                        <Text.ShortMediumText16 text={stringsRes.sos.enabled.title} />
                        <Text.Description14 text={stringsRes.sos.enabled.description} />
                        <Space value={1} type={Type.vertical} />
                        <Button
                            id="cancelSos"
                            className={clsx(classes.textButton, globalClasses.underline)}
                            onClick={onCancelSos}
                        >
                            {stringsRes.sos.enabled.cancel}
                        </Button>
                    </div>
                </div>
            )}

            {isRideTrackingPage && isOnboardPaid && isInProgress && (
                <InfoBox color="info" className={classes.infoBoxOnboard}>
                    <Text.H2 text={stringsRes.onboardPayment.title} />
                    <Text.Description14 text={stringsRes.onboardPayment.trackingDescription} />
                    <Text.Description14
                        text={stringsRes.common.knowMore}
                        fontStyle={{ textDecoration: 'underline' }}
                        onClick={onShowOnboardPayment}
                    />
                </InfoBox>
            )}

            {!isRideTrackingPage && <Space value={3} />}
            <div className={clsx(classes.mapContainerReservationCard, isRideTrackingPage && 'noMap')}>
                {!isRideTrackingPage && (
                    <MapView
                        stops={stops}
                        reservation={reservation}
                        waypoints={isBookingCancelled(status) ? [] : prepareWaypoints(getEncodedRoute(reservation))}
                        actionsCustom={{ zoomControl: false }}
                        roundedTopBorder={isInProgress}
                        refreshBounds={false}
                    />
                )}
                {(isInProgress || isTripHistoryPage) && !isCanceled && (
                    <div
                        className={clsx(classes.underMapContainerReservationCard, 'LCunderMapContainerReservationCard')}
                    >
                        {isTripHistoryPage ? (
                            <>
                                <div className={classes.flexRow}>
                                    <div className={globalClasses.flexCol}>
                                        {vehicle && (
                                            <>
                                                <Text.ShortBoldText16 text={`${vehicle.registration_number}`} />
                                                <Text.Description14
                                                    text={getVehicleInfos(vehicle, true)}
                                                    customColor="grey"
                                                />
                                            </>
                                        )}
                                        {driver && (
                                            <Text.Description14 text={getFullnameMaxed(driver)} customColor="grey" />
                                        )}
                                    </div>
                                </div>
                                {(hasRatingBlock || showRatingButton) && <Separator height={1} />}
                                {hasRatingBlock && (
                                    <>
                                        {driverRating?.score != null && (
                                            <div className={classes.ratingContainer}>
                                                <Text.H3 text={stringsRes.tripHistory.tipsAndRating.yourRating} />
                                                <Rating
                                                    name="rating"
                                                    value={driverRating?.score ?? 0}
                                                    size="medium"
                                                    readOnly={!canRateBooking(reservation) || !!driverRating?.score}
                                                    onChange={onAddTipsAndRating}
                                                />
                                            </div>
                                        )}

                                        {driverRating?.tags && (
                                            <div className={classes.ratingContainer}>
                                                <Text.H3 text={stringsRes.tripHistory.tipsAndRating.yourFeedback} />
                                                <div className={classes.tagsContainer}>
                                                    {driverRating.tags.map((tag) => (
                                                        <RadiusButton id={tag} key={tag} title={tag} mode="grey" />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {driverRating?.comment && (
                                            <div className={classes.ratingContainer}>
                                                <Text.H3 text={stringsRes.tripHistory.tipsAndRating.yourComment} />
                                                <Text.Description14
                                                    text={`${driverRating.comment}`}
                                                    customColor="grey"
                                                />
                                            </div>
                                        )}
                                        {tips && (
                                            <div className={classes.tipContainer}>
                                                <RoundedIcon
                                                    icon={IconAxel.HeartHands}
                                                    size="small"
                                                    customBackground="green"
                                                />
                                                <div>
                                                    <Text.ShortRegularText14
                                                        text={`${rps(
                                                            stringsRes.tripHistory.tipsAndRating.tipThanks.title,
                                                            {
                                                                amount: tips.amount.toFixed(2),
                                                            },
                                                        )}${stringsRes.booking.euroCurrency}`}
                                                    />
                                                    <Text.ShortRegularText14
                                                        text={stringsRes.tripHistory.tipsAndRating.tipThanks.toDriver}
                                                        customColor="grey"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                {showRatingButton && (
                                    <TextCenterButton
                                        className={classes.actionButton}
                                        title={
                                            driverRating?.score == null
                                                ? stringsRes.tripHistory.tipsAndRating.title
                                                : stringsRes.tripHistory.tipsAndRating.tipAdd
                                        }
                                        id="tipsAndRatingButton"
                                        onClick={onAddTipsAndRating}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                {isInProgress && (
                                    <ReservationTracking reservation={reservation} openModal={openModal} />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {isRideTrackingPage && isFinished && (
                <ReservationTracking reservation={reservation} openModal={openModal} />
            )}

            {((isRideTrackingPage && !isFinished) || !isRideTrackingPage) && (
                <>
                    {isRideTrackingPage && (
                        <>
                            {vehicle && driver && (
                                <>
                                    <Space value={3} />
                                    <Grid container className="driverInfos">
                                        <Grid item xs={8} className={classes.centerDetails}>
                                            <Text.H35 text={vehicle?.registration_number} />
                                            <Space />
                                            <Text.Description14
                                                text={getVehicleInfos(vehicle, true)}
                                                customColor="grey"
                                            />
                                            {driver && (
                                                <Text.Description14
                                                    text={`${stringsRes.booking.driver.name} ${driver.firstname}`}
                                                    customColor="grey"
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={4} className={classes.buttonContainer}>
                                            <a
                                                aria-label={stringsRes.profile.phone}
                                                href={`tel:${webview ? '//' : ''}0183777598`}
                                            >
                                                <RadiusButton
                                                    title=""
                                                    iconElement={
                                                        <SvgIcon
                                                            icon={IconAxel.Phone}
                                                            size="medium"
                                                            overwritePathStroke
                                                            stroke="white"
                                                        />
                                                    }
                                                    className={classes.roundedButton}
                                                    noSpace
                                                />
                                            </a>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            <Space value={2} />
                            <Separator height={1} />
                        </>
                    )}

                    {/* StopsTimeLine */}
                    <Space value={3} />
                    <Text.H35 text={stringsRes.booking.pickup} />
                    <Space value={2} />
                    <StopsTimeLineList
                        stops={stops}
                        provision={provision}
                        pancarte={hasPancarte}
                        noLabel
                        noTime={isMobileSize}
                    />
                    <Space value={2} />
                    {!isTripHistoryPage && pickup?.address?.meeting_points && (
                        <>
                            <div className={classes.planeDetailsContainer}>
                                {!isOther && (
                                    <>
                                        <Text.H35
                                            text={
                                                isPlane ? stringsRes.booking.planeTitle : stringsRes.booking.trainTitle
                                            }
                                        />
                                        <ReservationLabelInfos
                                            label={
                                                isPlane
                                                    ? stringsRes.booking.planePlaceholder
                                                    : stringsRes.booking.trainPlaceholder
                                            }
                                            description={
                                                pickup.connection_details?.number ??
                                                `${isNotDispatched ? stringsRes.common.add : ''}`
                                            }
                                            onEdit={isNotDispatched && onChangePOINumber}
                                            isMeetingPoint
                                            id="changePOINumber"
                                            endSpaceValue={null}
                                        />
                                    </>
                                )}
                                <ReservationLabelInfos
                                    label={stringsRes.booking.meetingPointRecap}
                                    description={
                                        (hasPancarte
                                            ? pickup.address.meeting_points_nameBoard
                                            : pickup.address.meeting_points
                                        ).find((mp) => mp.id === pickup.meeting_point_id)?.name
                                    }
                                    onEdit={isNotDispatched && onChangeMeetingPoint}
                                    isMeetingPoint
                                    id="changeMeetingPoint"
                                    endSpaceValue={null}
                                />
                                {hasPancarte && (
                                    <ReservationLabelInfos
                                        // iconElement={<SvgIcon icon={IconCommon.Pancarte} size="small" />}
                                        label={stringsRes.booking.pancarte}
                                        description={stringsRes.common.yes}
                                        isMeetingPoint
                                        endSpaceValue={null}
                                    />
                                )}
                            </div>
                            <Space value={3} />
                        </>
                    )}
                    {CustomSeparator}

                    {/* Details */}
                    {!recurrent && (
                        <>
                            <Space value={3} />
                            <div className={classes.headerRow}>
                                <span
                                    className={clsx(globalClasses.flexRow, globalClasses.action)}
                                    onClick={() => {
                                        copyToClipboard(reservation.number);
                                    }}
                                >
                                    <Text.H35
                                        text={`${rps(stringsRes.booking.number, { number: reservation.number })}`}
                                    />
                                    <Space value={1} type={Type.horizontal} />
                                    <SvgIcon
                                        icon={IconAxel.Copy}
                                        size="small"
                                        fill="none"
                                        stroke={isDarkMode ? darkColors.black : colors.black}
                                        overwritePathStroke
                                    />
                                </span>
                                {isCanceled ? (
                                    <Text.ShortMediumText14
                                        text={`${stringsRes.common.cancelled} ${
                                            isCanceledNoCharge ? '' : ` - ${price}`
                                        }`}
                                        customColor="red"
                                        className={classes.bolder}
                                    />
                                ) : (
                                    <Text.H25 text={price} />
                                )}
                            </div>
                        </>
                    )}
                    <div className={classes.headerRow}>
                        {/* 
                        {isNotDispatched && !isTripHistoryPage && (
                        <Button
                            id="edit"
                            className={classes.reservationLabelInfosButton}
                            onClick={onEditReservationService}
                        >
                            {stringsRes.booking.edit}
                        </Button>
                        )} 
                     */}
                    </div>
                    {/* 
                        <div className={classes.headerRow}>
                            {!isInProgress && !isCanceled && createTextFromStatus()}
                        </div> 
                    */}

                    <div>
                        <Space value={2} />
                        <ReservationLabelInfos
                            iconElement={<RoundedIcon customBackground="grey" icon={IconAxel.CarSide} size="small" />}
                            label={service?.name}
                            alwaysVisible
                            id="service"
                        />
                        {recurrent && (
                            <ReservationLabelInfos
                                iconElement={
                                    <RoundedIcon
                                        customBackground="grey"
                                        icon={IconAxel.RecurrenceRound}
                                        size="small"
                                        stroke="unset"
                                    />
                                }
                                label={cronStringToDateAndDays(schedule)}
                                alwaysVisible
                                id="recurrence"
                            />
                        )}
                        <PassengerList pro={isPro} passengers={riders} onEdit={isNotDispatched && onChangeRider} />
                        {/* The tracking page says it in its own banner, no need to repeat it here */}
                        {isOnboardPaid && !isRideTrackingPage && (
                            <ReservationLabelInfos
                                iconElement={<RoundedIcon customBackground="grey" icon={IconAxel.Obp} size="small" />}
                                label={stringsRes.onboardPayment.title}
                                alwaysVisible
                                id="onboardPayment"
                            />
                        )}
                        {priceDetails?.promotion && (
                            <ReservationLabelInfos
                                iconElement={
                                    <RoundedIcon customBackground="grey" icon={IconAxel.TicketPromo} size="small" />
                                }
                                label={priceDetails?.promotion.promo_name} // stringsRes.promo.codeApplied
                                alwaysVisible
                            />
                        )}
                        {isPro && costCenter && (
                            <ReservationLabelInfos
                                iconElement={
                                    <RoundedIcon customBackground="grey" icon={IconAxel.FolderLock} size="small" />
                                }
                                label={costCenter?.name} // stringsRes.profile.costCenter
                                onEdit={isNotDispatched && onChangeCostCenter}
                                alwaysVisible
                                id="changeCostCenter"
                            />
                        )}

                        {group && (
                            <ReservationLabelInfos
                                iconElement={<RoundedIcon customBackground="grey" icon={IconAxel.Users} size="small" />}
                                label={group?.name} // stringsRes.admin.groups.group
                                alwaysVisible
                            />
                        )}

                        {isGreen && (
                            <>
                                <Space value={1} />
                                <InfoBox color="green" className={classes.infoBoxGreen}>
                                    <Text.H2 text={stringsRes.booking.green.title} customColor="bothWhite" />
                                    <Text.Description14
                                        text={stringsRes.booking.green.details}
                                        customColor="bothWhite"
                                    />
                                    <Text.Description14
                                        text={stringsRes.common.knowMore}
                                        customColor="bothWhite"
                                        fontStyle={{ textDecoration: 'underline' }}
                                        onClick={onNavigateToGreenMore}
                                    />
                                </InfoBox>
                            </>
                        )}

                        {isWicked && <WickedImage type={ImageEvent.WickedDetails} />}
                    </div>
                    {/* Cancel box orange */}
                    {/* 
                        {isTripHistoryPage && isCanceled && !isCanceledNoCharge && (
                            <>
                                <Space value={3} />
                                <InfoBox color="warning" className={classes.infoBox}>
                                    <Text.Description14
                                        text={rps(stringsRes.tripHistory.canceledWithCharge, { price })}
                                        customColor="orange"
                                    />
                                </InfoBox>
                            </>
                        )} 
                    */}
                    {isAssistance && (
                        <>
                            <Space value={3} />
                            <Separator />
                            <Space value={3} />
                            <Text.H35 text={stringsRes.booking.priceDetail} />
                            <Space value={1} />
                            <div className={classes.headerRow}>
                                <Text.Description14 text={stringsRes.booking.voucher.price.covered} />
                                <Text.Description14
                                    text={`${reservation?.price_details?.covered_by_third_party?.amount_with_tax?.toFixed(
                                        2,
                                    )}${stringsRes.booking.euroCurrency}`}
                                />
                            </div>
                            <div className={classes.headerRow}>
                                <Text.Description14 text={stringsRes.booking.voucher.price.remaining} />
                                <Text.Description14
                                    text={`${(
                                        reservation?.price_details?.client_final_price -
                                        reservation?.price_details?.covered_by_third_party?.amount_with_tax
                                    )?.toFixed(2)}${stringsRes.booking.euroCurrency}`}
                                />
                            </div>
                        </>
                    )}
                </>
            )}

            <Space value={3} />

            {/* Entries actions top */}
            {menuEntries?.length > 0 && (
                <>
                    <Separator height={1} />
                    <EditButtonList buttonsEntries={menuEntries} />
                </>
            )}
            {CustomSeparator}

            {/* More Details */}
            <AccordionBox title={stringsRes.booking.moreDetails} styleHistory>
                {/* Info Reservation */}
                {isPro && booker && (
                    <ReservationLabelInfos
                        label={stringsRes.reservation.booked}
                        description={getFullnameMaxed(booker)}
                    />
                )}
                <ReservationLabelInfos
                    label={stringsRes.booking.creationDate}
                    description={formatDateFromTimestamp(creationDate)}
                />

                {!isPro ? (
                    <ReservationLabelInfos
                        label={stringsRes.booking.paymentTitle}
                        description={getMaskedCard((paymentMethod as any)?.masked_number)}
                        onEdit={null} /* isNotDispatched && onChangePayment */
                        id="changeCard"
                    />
                ) : (
                    <ReservationLabelInfos
                        label={stringsRes.booking.paymentTitle}
                        description={convertPaymentTypePolicy(paymentMethod, paymentPolicy)}
                        onEdit={null} /* isNotDispatched && onChangePayment */
                        id="changePayment"
                    />
                )}

                {driverNote && (
                    <ReservationLabelInfos label={stringsRes.booking.msgToDriverShort} description={driverNote} />
                )}

                {(isTripHistoryPage || recurrent) && (
                    <ReservationLabelInfos label={stringsRes.booking.rideReasonTitle} description={rideReason} />
                )}

                {isTripHistoryPage && (
                    <>
                        {pickup?.address?.meeting_points && (
                            <>
                                {!isOther && (
                                    <ReservationLabelInfos
                                        label={
                                            isPlane
                                                ? stringsRes.booking.planePlaceholder
                                                : stringsRes.booking.trainPlaceholder
                                        }
                                        description={pickup.connection_details?.number}
                                        id="changePOINumber"
                                    />
                                )}
                                <ReservationLabelInfos
                                    label={stringsRes.booking.meetingPointRecap}
                                    description={
                                        (hasPancarte
                                            ? pickup.address.meeting_points_nameBoard
                                            : pickup.address.meeting_points
                                        ).find((mp) => mp.id === pickup.meeting_point_id)?.name
                                    }
                                    id="changeMeetingPoint"
                                />
                                {hasPancarte && (
                                    <ReservationLabelInfos
                                        // iconElement={<SvgIcon icon={IconCommon.Pancarte} size="small" />}
                                        label={stringsRes.booking.pancarte}
                                        description={stringsRes.common.yes}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
                <Space value={1} />
            </AccordionBox>
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        headerRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        filter: {
            color: theme.palette.secondary.light,
        },
        label: {
            marginLeft: 8,
            color: 'inherit',
        },
        emergencyContainer: {
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            borderRadius: 10,
            border: `1px solid ${theme.palette.colors.background.menuSeparator}`,
            padding: 20,
            gap: 12,
            marginBottom: theme.spacing(2),
            alignItems: 'center',
        },
        mapContainerReservationCard: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            borderRadius: 10,
            border: `1px solid ${theme.palette.colors.background.menuSeparator}`,
            '&:not(.noMap) > div:first-child': {
                height: '280px !important',
                minHeight: '280px !important',
                maxHeight: '280px !important',
            },
            '&:not(.noMap) > div:first-child > div > div': {
                // borderRadius: 10,
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
            },
            '&:empty': {
                display: 'none',
            },
        },
        underMapContainerReservationCard: {
            display: 'flex',
            padding: '24px 32px',
            gap: 20,
            flexDirection: 'column',
            '& > div[class*="reservationTrackingContainer"]': {
                padding: 'unset',
                border: 'unset',
            },
            '@media (max-width: 600px)': {
                gap: 16,
            },
        },
        ratingContainer: {
            display: 'flex',
            gap: 8,
            flexDirection: 'column',
        },
        tagsContainer: {
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            '& button:hover': {
                cursor: 'default',
            },
        },
        tipContainer: {
            padding: '16px 12px 16px 20px',
            gap: 12,
            borderRadius: 4,
            border: `1px solid ${theme.palette.colors.background.menuSeparator}`,
            display: 'flex',
        },
        flexRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        reservationLabelInfosButton: {
            // color: theme.palette.secondary.light,
            fontWeight: 400,
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        planeDetailsContainer: {
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            gap: 12,
            borderRadius: 4,
            backgroundColor: theme.palette.colors.background.primaryElement,
            border: `1px solid ${theme.palette.colors.background.menuSeparator}`,
        },
        infoBox: {
            marginLeft: 'unset',
            marginRight: 'unset',
        },
        infoBoxOnboard: {
            gap: 4,
            marginLeft: 'unset !important',
            marginRight: 'unset !important',
            marginBottom: `${theme.spacing(2)} !important`,
            flexDirection: 'column',
            alignItems: 'flex-start !important',
            padding: '16px 20px !important',
        },
        infoBoxGreen: {
            gap: 4,
            marginLeft: 'unset !important',
            marginRight: 'unset !important',
            flexDirection: 'column',
            alignItems: 'flex-start !important',
            padding: '16px 20px !important',
        },
        passengersEditModal: {
            height: '75%',
        },
        bolder: {
            fontWeight: '700 !important',
        },

        centerDetails: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'start',
        },
        buttonContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            alignSelf: 'center',
            columnGap: 8,
        },
        roundedButton: {
            padding: 14,
            minWidth: 'unset',
            // The phone icon is hardcoded white and `secondary.main` turns near-white in dark mode, which is
            // how this button ended up white on white. Deep blue keeps a dark ground under the icon there.
            backgroundColor: callButtonBackground(theme),
            border: 'unset',
            '&:hover': {
                backgroundColor: darken(callButtonBackground(theme), 0.05),
                border: 'unset',
            },
        },
        actionButton: {
            borderRadius: 10,
            backgroundColor: theme.palette.secondary.light,
            '&:hover': {
                backgroundColor: lighten(theme.palette.secondary.light, 0.1),
            },
        },

        textButton: {
            padding: 0,
            fontWeight: 400,
            justifyContent: 'flex-start',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    }),
);

export default memo(ReservationCard);
